import {
  axiosRequestPath,
  describeAxiosFailureWithoutHttpStatus,
  getAxiosEffectiveHttpStatus,
  getAxiosFingerprintStatusKey,
  normalizePathForDisplay
} from '@global/monitoring/axios-helpers';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import React from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  Routes,
  useLocation,
  useNavigationType
} from 'react-router-dom';

const initSentry = () => {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes
      })
    ],
    tunnel: import.meta.env.VITE_SENTRY_PROXY,
    // Performance Monitoring
    tracesSampleRate: import.meta.env.VITE_ENV === 'development' ? 0 : 1.0, // Capture 100% of the transactions
    environment: import.meta.env.VITE_ENV,
    // Session Replay
    replaysSessionSampleRate: import.meta.env.VITE_ENV === 'development' ? 0 : 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: import.meta.env.VITE_ENV === 'development' ? 0 : 1.0, // If you"re not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    beforeSend(event, hint) {
      const original = hint.originalException;

      if (axios.isAxiosError(original)) {
        const httpStatus = getAxiosEffectiveHttpStatus(original);
        const statusKey = getAxiosFingerprintStatusKey(original, httpStatus);
        const method = (original.config?.method ?? 'GET').toUpperCase();
        const path = normalizePathForDisplay(axiosRequestPath(original.config));

        const readable =
          httpStatus != null
            ? `HTTP ${httpStatus} ${method} ${path}`
            : `${method} ${path} (${describeAxiosFailureWithoutHttpStatus(original)})`;

        const exception = event.exception?.values?.[0];
        if (exception) {
          exception.value = readable;
        } else if (event.message) {
          event.message = readable;
        }

        event.fingerprint = ['axios-http', method, path, statusKey];

        event.tags = {
          ...(event.tags || {}),
          axios_method: method,
          http_status: statusKey,
          api_path: path
        };

        return event;
      }

      return event;
    }
  });
};

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

export { initSentry, SentryRoutes };
